import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ChevronDown, 
  ChevronRight, 
  ChevronUp, 
  Trash2, 
  RefreshCw, 
  BookOpen, 
  FolderOpen, 
  Folder, 
  Calendar,
  FileText
} from 'lucide-react';
import { authenticatedFetch } from '../../utils/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

const ManageLessonNotes = () => {
  const [lessonNotes, setLessonNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [expandedClasses, setExpandedClasses] = useState({});
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadLessonNotes();
  }, []);

  const loadLessonNotes = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/admin/lesson-notes/all`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLessonNotes(data.lesson_notes || []);
      } else {
        console.error('Failed to load lesson notes');
      }
    } catch (error) {
      console.error('Error loading lesson notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleNoteDetails = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const toggleClass = (className) => {
    setExpandedClasses(prev => ({
      ...prev,
      [className]: !prev[className]
    }));
  };

  const toggleWeek = (className, week) => {
    const key = `${className}-${week}`;
    setExpandedWeeks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isClassExpanded = (className) => expandedClasses[className] ?? false;
  const isWeekExpanded = (className, week) => expandedWeeks[`${className}-${week}`] ?? false;

  const expandAll = () => {
    const allClasses = {};
    const allWeeks = {};
    const grouped = organizedNotes();
    
    Object.keys(grouped).forEach(className => {
      allClasses[className] = true;
      Object.keys(grouped[className]).forEach(week => {
        allWeeks[`${className}-${week}`] = true;
      });
    });
    
    setExpandedClasses(allClasses);
    setExpandedWeeks(allWeeks);
  };

  const collapseAll = () => {
    setExpandedClasses({});
    setExpandedWeeks({});
  };

  const handleDeleteClick = (note) => {
    setLessonToDelete(note);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!lessonToDelete) return;
    
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/admin/lesson-notes/${lessonToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setLessonNotes(prev => prev.filter(note => note.id !== lessonToDelete.id));
        setDeleteDialogOpen(false);
        setLessonToDelete(null);
      } else {
        console.error('Failed to delete lesson note');
      }
    } catch (error) {
      console.error('Error deleting lesson note:', error);
    }
  };

  // Group and sort lesson notes by class, then by week
  const organizedNotes = () => {
    const sorted = [...lessonNotes].sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });

    const grouped = {};
    sorted.forEach(note => {
      const className = note.class_name || 'Unknown Class';
      const week = note.week || 'Unknown';
      
      if (!grouped[className]) {
        grouped[className] = {};
      }
      if (!grouped[className][week]) {
        grouped[className][week] = [];
      }
      grouped[className][week].push(note);
    });

    return grouped;
  };

  // Count notes in a class folder
  const countClassNotes = (className, grouped) => {
    let count = 0;
    Object.values(grouped[className] || {}).forEach(weekNotes => {
      count += weekNotes.length;
    });
    return count;
  };

  // Sort class names properly (KG1, KG2, Primary 1-6, JHS 1-3)
  const sortClassNames = (classes) => {
    const order = {
      'KG 1': 1, 'KG1': 1, 'KG 2': 2, 'KG2': 2,
      'Primary 1': 3, 'Primary 2': 4, 'Primary 3': 5, 
      'Primary 4': 6, 'Primary 5': 7, 'Primary 6': 8,
      'JHS 1': 9, 'JHS 2': 10, 'JHS 3': 11
    };
    
    return classes.sort((a, b) => {
      const orderA = order[a] || 100;
      const orderB = order[b] || 100;
      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-600">Loading lesson notes...</p>
        </CardContent>
      </Card>
    );
  }

  const grouped = organizedNotes();
  const sortedClasses = sortClassNames(Object.keys(grouped));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Manage Uploaded Lesson Notes
          </CardTitle>
          <CardDescription>
            View and manage all lesson notes organized by class and week
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <p className="text-sm text-gray-600">
              Total: <span className="font-semibold">{lessonNotes.length}</span> lesson notes in <span className="font-semibold">{sortedClasses.length}</span> classes
            </p>
            <div className="flex gap-2">
              <Button onClick={expandAll} variant="outline" size="sm">
                <FolderOpen className="h-4 w-4 mr-1" />
                Expand All
              </Button>
              <Button onClick={collapseAll} variant="outline" size="sm">
                <Folder className="h-4 w-4 mr-1" />
                Collapse All
              </Button>
              <Button onClick={loadLessonNotes} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>

          {lessonNotes.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No lesson notes uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedClasses.map(className => {
                const classNoteCount = countClassNotes(className, grouped);
                const weekCount = Object.keys(grouped[className]).length;
                const isOpen = isClassExpanded(className);
                
                return (
                  <Collapsible
                    key={className}
                    open={isOpen}
                    onOpenChange={() => toggleClass(className)}
                  >
                    {/* Class Folder Header */}
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-150 rounded-lg cursor-pointer border border-blue-200 transition-all">
                        {isOpen ? (
                          <ChevronDown className="h-5 w-5 text-blue-600" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-blue-600" />
                        )}
                        {isOpen ? (
                          <FolderOpen className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Folder className="h-5 w-5 text-blue-500" />
                        )}
                        <span className="font-semibold text-blue-800 flex-1">{className}</span>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="bg-white text-blue-700">
                            {weekCount} {weekCount === 1 ? 'week' : 'weeks'}
                          </Badge>
                          <Badge className="bg-blue-600 text-white">
                            {classNoteCount} {classNoteCount === 1 ? 'note' : 'notes'}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    
                    {/* Week Folders Inside Class */}
                    <CollapsibleContent>
                      <div className="ml-6 mt-2 space-y-2 border-l-2 border-blue-200 pl-4">
                        {Object.keys(grouped[className])
                          .sort((a, b) => {
                            const weekA = parseInt(a) || 0;
                            const weekB = parseInt(b) || 0;
                            return weekA - weekB;
                          })
                          .map(week => {
                            const weekNotes = grouped[className][week];
                            const isWeekOpen = isWeekExpanded(className, week);
                            
                            return (
                              <Collapsible
                                key={`${className}-${week}`}
                                open={isWeekOpen}
                                onOpenChange={() => toggleWeek(className, week)}
                              >
                                {/* Week Folder Header */}
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-2 p-2.5 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-lg cursor-pointer border border-green-200 transition-all">
                                    {isWeekOpen ? (
                                      <ChevronDown className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-green-600" />
                                    )}
                                    <Calendar className="h-4 w-4 text-green-600" />
                                    <span className="font-medium text-green-800 flex-1">Week {week}</span>
                                    <Badge className="bg-green-600 text-white text-xs">
                                      {weekNotes.length} {weekNotes.length === 1 ? 'note' : 'notes'}
                                    </Badge>
                                  </div>
                                </CollapsibleTrigger>
                                
                                {/* Lesson Notes Inside Week */}
                                <CollapsibleContent>
                                  <div className="ml-6 mt-2 space-y-2 border-l-2 border-green-200 pl-4 pb-2">
                                    {weekNotes.map(note => (
                                      <Card 
                                        key={note.id} 
                                        className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow bg-white"
                                      >
                                        <CardContent className="p-3">
                                          <div className="flex flex-col gap-2">
                                            {/* Note Header */}
                                            <div className="flex justify-between items-start gap-2">
                                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <FileText className="h-4 w-4 text-purple-500 shrink-0" />
                                                <span className="font-medium text-gray-800 truncate">
                                                  {note.file_name || note.title || 'Lesson Note'}
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-1 shrink-0">
                                                <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                                                  {note.education_level?.toUpperCase() || 'JHS'}
                                                </Badge>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick(note);
                                                  }}
                                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                                >
                                                  <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                              </div>
                                            </div>

                                            {/* Note Info */}
                                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                                              <span className="font-medium text-purple-700">{note.subject || note.class_subject || 'Subject'}</span>
                                              <span>•</span>
                                              <span>Term {note.term || 1}</span>
                                              <span>•</span>
                                              <span>{new Date(note.created_at).toLocaleDateString()}</span>
                                            </div>

                                            {/* Expandable Details */}
                                            {expandedNotes[note.id] && (
                                              <div className="mt-2 p-2 bg-gray-50 rounded-md text-xs space-y-1">
                                                <p><strong>File Type:</strong> {note.file_type?.toUpperCase() || 'PDF'}</p>
                                                {note.file_url && (
                                                  <p className="break-all"><strong>File URL:</strong> {note.file_url}</p>
                                                )}
                                                {note.term_id && (
                                                  <p><strong>Term ID:</strong> {note.term_id}</p>
                                                )}
                                                <p><strong>Uploaded:</strong> {new Date(note.created_at).toLocaleString()}</p>
                                              </div>
                                            )}

                                            {/* Show More/Less Button */}
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => toggleNoteDetails(note.id)}
                                              className="w-full h-7 text-xs text-gray-500"
                                            >
                                              {expandedNotes[note.id] ? (
                                                <>
                                                  <ChevronUp className="h-3 w-3 mr-1" />
                                                  Show Less
                                                </>
                                              ) : (
                                                <>
                                                  <ChevronDown className="h-3 w-3 mr-1" />
                                                  Show More
                                                </>
                                              )}
                                            </Button>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            );
                          })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lesson Note?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{lessonToDelete?.file_name || 'this lesson note'}&quot;?
              <br />
              <span className="text-red-600 font-medium">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManageLessonNotes;
